import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabaseClient';
import { UserCredits } from '../types/credits';

interface CreditContextType {
  credits: UserCredits | null;
  loading: boolean;
  error: string | null;
  refreshCredits: () => Promise<void>;
  useCredits: (amount: number, description: string, referenceId?: string, referenceType?: string) => Promise<boolean>;
  addCredits: (amount: number, description: string, referenceId?: string, referenceType?: string) => Promise<void>;
}

const CreditContext = createContext<CreditContextType | undefined>(undefined);

interface CreditProviderProps {
  children: ReactNode;
}

export const CreditProvider: React.FC<CreditProviderProps> = ({ children }) => {
  const [credits, setCredits] = useState<UserCredits | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCredits = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setCredits(null);
        return;
      }

      const { data, error: fetchError } = await supabase
        .from('user_credits')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (fetchError) {
        if (fetchError.code === 'PGRST116') {
          // No credits record found, create one with 6 free credits
          const { data: newCredits, error: createError } = await supabase
            .from('user_credits')
            .insert({
              user_id: user.id,
              credits_balance: 6
            })
            .select()
            .single();

          if (createError) {
            throw createError;
          }

          // Create initial bonus transaction
          await supabase
            .from('credit_transactions')
            .insert({
              user_id: user.id,
              type: 'BONUS',
              amount: 6,
              description: 'Welcome bonus - 6 free credits'
            });

          setCredits(newCredits);
        } else {
          throw fetchError;
        }
      } else {
        setCredits(data);
      }
    } catch (err) {
      console.error('Error fetching credits:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch credits');
    } finally {
      setLoading(false);
    }
  };

  const refreshCredits = async () => {
    await fetchCredits();
  };

  const useCredits = async (
    amount: number, 
    description: string, 
    referenceId?: string, 
    referenceType?: string
  ): Promise<boolean> => {
    if (!credits || credits.credits_balance < amount) {
      return false;
    }

    try {
      const newBalance = credits.credits_balance - amount;
      
      // Update credits balance
      const { error: updateError } = await supabase
        .from('user_credits')
        .update({
          credits_balance: newBalance,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', credits.user_id);

      if (updateError) {
        throw updateError;
      }

      // Create transaction record
      const { error: transactionError } = await supabase
        .from('credit_transactions')
        .insert({
          user_id: credits.user_id,
          type: 'USAGE',
          amount: -amount,
          description,
          reference_id: referenceId,
          reference_type: referenceType
        });

      if (transactionError) {
        throw transactionError;
      }

      // Update local state
      setCredits(prev => prev ? {
        ...prev,
        credits_balance: newBalance
      } : null);

      return true;
    } catch (err) {
      console.error('Error using credits:', err);
      setError(err instanceof Error ? err.message : 'Failed to use credits');
      return false;
    }
  };

  const addCredits = async (
    amount: number, 
    description: string, 
    referenceId?: string, 
    referenceType?: string
  ) => {
    if (!credits) return;

    try {
      const newBalance = credits.credits_balance + amount;
      
      // Update credits balance
      const { error: updateError } = await supabase
        .from('user_credits')
        .update({
          credits_balance: newBalance,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', credits.user_id);

      if (updateError) {
        throw updateError;
      }

      // Create transaction record
      const { error: transactionError } = await supabase
        .from('credit_transactions')
        .insert({
          user_id: credits.user_id,
          type: 'PURCHASE',
          amount: amount,
          description,
          reference_id: referenceId,
          reference_type: referenceType
        });

      if (transactionError) {
        throw transactionError;
      }

      // Update local state
      setCredits(prev => prev ? {
        ...prev,
        credits_balance: newBalance
      } : null);
    } catch (err) {
      console.error('Error adding credits:', err);
      setError(err instanceof Error ? err.message : 'Failed to add credits');
    }
  };

  useEffect(() => {
    fetchCredits();
  }, []);

  const value: CreditContextType = {
    credits,
    loading,
    error,
    refreshCredits,
    useCredits,
    addCredits
  };

  return (
    <CreditContext.Provider value={value}>
      {children}
    </CreditContext.Provider>
  );
};

export const useCredits = (): CreditContextType => {
  const context = useContext(CreditContext);
  if (context === undefined) {
    throw new Error('useCredits must be used within a CreditProvider');
  }
  return context;
};
